import * as Yup from 'yup';
import { useContext, useEffect, useState } from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
// material
import { Stack, TextField, Button, InputAdornment, Box, Link, Grid, Typography, MenuItem, useTheme, useMediaQuery } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import axios from '../../util/axios';
import token from '../../util/Token';
import { Context } from '../../context';
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

// ----------------------------------------------------------------------

export default function RequestChargerForm() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, auth } = useContext(Context);
  const { userDetail } = user;
  const isAuth = auth && auth.authenticated && auth.authenticated.isAuth;
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState();
  const [purpose, setPurpose] = useState(null);
  const handlePurposeChange = (event, value) => {
    setPurpose(value);
  };

  const resetPasswordSchema = Yup.object().shape({
    name: Yup.string().required('Please enter your name'),
    email: Yup.string().email('Invalid email address').required('Please enter your email'),
    phone: Yup.string().matches(/^[0-9]+$/, 'Must be only digits').length(10, 'Must be exactly 10 digits').required('Please enter your phone number'),
    purpose: Yup.string().required('Please select an installation purpose').nullable(),
    make: purpose === 'PERSONAL' ? Yup.string().required('Please select a make') : Yup.string(),
    model: purpose === 'PERSONAL' ? Yup.string().required('Please select a model') : Yup.string(),
    charger: purpose === 'COMMERCIAL' ? Yup.string().required('Please enter your requirements') : Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      name: userDetail?.name || '',
      email: userDetail?.email || '',
      phone: '',
      purpose: '',
      make: '',
      model: '',
      charger: ''
    },
    validationSchema: resetPasswordSchema,
    onSubmit: (requestChargerData, { setSubmitting, resetForm }) => {
      // if (!userDetail) {
      //   setError("Invalid session");
      //   setSuccessMessage("");
      //   setSubmitting(false);
      //   return;
      // }
      if (userDetail && userDetail._id) {
        requestChargerData['user'] = userDetail?._id;
      }
      if (purpose && purpose === "COMMERCIAL") {
        delete requestChargerData['make'];
        delete requestChargerData['model'];
      }
      (async function () {
        try {
          const registerHelper = async () => {
            console.log(process.env.REACT_APP_API_END_POINT);
            const response = await axios(`${process.env.REACT_APP_API_END_POINT}util/request-charger`, {
              method: 'post',
              data: requestChargerData
            });
            const resetPasswordResponse = response.data.data;
            setSubmitting(false);
            resetForm();
            setError('');
            setSuccessMessage("Request submitted successfully.");
          };
          await registerHelper();
        } catch (error) {
          setSubmitting(false);
          if (error.response && error.response.data && error.response.data.message) {
            setError(error.response.data.message);
            setSuccessMessage("");
          }
        }
      })();
      //navigate('/dashboard', { replace: true });
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, resetForm, setFieldValue } = formik;



  useEffect(() => {
    setFieldValue('purpose', purpose, false);
  }, [purpose]);

  useEffect(() => {
    if (userDetail) {
      setFieldValue("name", userDetail["name"] || "", false);
      setFieldValue("email", userDetail["email"] || "", false);
    }
  }, [userDetail]);

  return (
    <FormikProvider value={formik}>
      {error && (
        <Box sx={{ color: '#d32f2f', bgcolor: 'rgba(211, 47, 47, 0.08)', border: '1px solid rgba(211, 47, 47, 0.3)', borderRadius: 2, px: 2, py: 1.5, mb: 2, fontSize: '0.875rem', fontWeight: 500 }}>
          {error}
        </Box>
      )}
      {successMessage && (
        <Box sx={{ color: '#2e7d32', bgcolor: 'rgba(46, 125, 50, 0.08)', border: '1px solid rgba(46, 125, 50, 0.3)', borderRadius: 2, px: 2, py: 1.5, mb: 2, fontSize: '0.875rem', fontWeight: 500 }}>
          {successMessage}
        </Box>
      )}
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          
          {/* Installation Purpose Selection */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={600} color="text.secondary" gutterBottom>
              Installation Purpose
            </Typography>
            <ToggleButtonGroup
              color="primary"
              value={purpose}
              exclusive
              onChange={handlePurposeChange}
              fullWidth
              orientation={isSmallScreen ? "vertical" : "horizontal"}
              sx={(theme) => ({ 
                border: 'none',
                '& .MuiToggleButtonGroup-grouped': {
                  border: 'none',
                  m: 0
                },
                '& .MuiToggleButton-root': { 
                  margin: 0,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '13px',
                  padding: '12px 16px',
                  minHeight: 44,
                  border: `2px solid ${theme.palette.grey[300]} !important`,
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.background.paper,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.light}05 100%)`,
                    borderColor: `${theme.palette.primary.light} !important`,
                    color: theme.palette.primary.main,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px 0 ${theme.palette.primary.main}20`,
                    zIndex: 1,
                  },
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%) !important`,
                    color: 'white !important',
                    borderColor: `${theme.palette.primary.main} !important`,
                    boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
                    zIndex: 2,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%) !important`,
                      boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}60`,
                      transform: 'translateY(-2px)',
                    },
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  ...(isSmallScreen ? {
                    // vertical group
                    '&:not(:first-of-type)': {
                      marginTop: '-2px',
                    },
                    '&:first-of-type': {
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0
                    },
                    '&:last-of-type': {
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0
                    },
                    '&:not(:first-of-type):not(:last-of-type)': {
                      borderRadius: 0
                    }
                  } : {
                    // horizontal group
                    '&:not(:first-of-type)': {
                      marginLeft: '-2px',
                    },
                    '&:first-of-type': {
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0
                    },
                    '&:last-of-type': {
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0
                    },
                    '&:not(:first-of-type):not(:last-of-type)': {
                      borderRadius: 0
                    }
                  })
                }
              })}
            >
              <ToggleButton value="PERSONAL">Personal</ToggleButton>
              <ToggleButton value="COMMERCIAL">Commercial</ToggleButton>
            </ToggleButtonGroup>
            {touched.purpose && errors.purpose && (
              <Typography variant="caption" sx={{ color: "error.main", mt: 1, display: 'block' }}>
                {errors.purpose}
              </Typography>
            )}
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Name"
              disabled={isAuth}
              {...getFieldProps('name')}
              error={Boolean(touched.name && errors.name)}
              helperText={touched.name && errors.name}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isAuth ? 'action.hover' : 'background.paper' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              disabled={isAuth}
              {...getFieldProps('email')}
              error={Boolean(touched.email && errors.email)}
              helperText={touched.email && errors.email}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isAuth ? 'action.hover' : 'background.paper' } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone Number"
              {...getFieldProps('phone')}
              inputProps={{ maxLength: 10 }}
              error={Boolean(touched.phone && errors.phone)}
              helperText={touched.phone && errors.phone}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
            />
          </Grid>

          {/* Conditional Fields based on Purpose */}
          {(purpose === "PERSONAL") && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Make of the Vehicle"
                  {...getFieldProps('make')}
                  error={Boolean(touched.make && errors.make)}
                  helperText={touched.make && errors.make}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' }, textAlign: 'left' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Model of the Vehicle"
                  {...getFieldProps('model')}
                  error={Boolean(touched.model && errors.model)}
                  helperText={touched.model && errors.model}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' }, textAlign: 'left' }}
                />
              </Grid>
            </>
          )}

          {(purpose === "COMMERCIAL") && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Enter your requirements"
                {...getFieldProps('charger')}
                error={Boolean(touched.charger && errors.charger)}
                helperText={touched.charger && errors.charger}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
              />
            </Grid>
          )}

          {/* Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                variant="outlined" 
                component={RouterLink} 
                to="/"
                size="large"
                sx={{ px: 4, borderRadius: 2, color: 'text.secondary', borderColor: 'divider' }}
              >
                Cancel
              </Button>
              <LoadingButton 
                type="submit" 
                variant="contained" 
                size="large"
                loading={isSubmitting}
                sx={{ 
                  px: 5, 
                  borderRadius: 2,
                  background: `linear-gradient(135deg, var(--app-color) 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 8px 16px 0 rgba(25, 118, 210, 0.24)`,
                  '&:hover': {
                    boxShadow: `0 8px 16px 0 rgba(25, 118, 210, 0.5)`,
                  }
                }}
              >
                Submit Request
              </LoadingButton>
            </Box>
          </Grid>

        </Grid>
      </Form>
    </FormikProvider>
  );
}
